import { CATEGORIES } from "../categories";

/** Cabla apertura/chiusura del panel di un custom select (.select). Chiude su
 * click fuori dal componente (entro `boundary`, di norma il dialog). Ritorna
 * `setOpen` per chiudere da fuori (es. dopo una scelta single-select). */
export function wireDropdownToggle(root: HTMLElement, boundary: HTMLElement): (open: boolean) => void {
  const trigger = root.querySelector<HTMLButtonElement>(".select__trigger")!;
  const panel = root.querySelector<HTMLDivElement>(".select__panel")!;
  const setOpen = (open: boolean): void => {
    root.dataset.open = String(open);
    trigger.setAttribute("aria-expanded", String(open));
    panel.hidden = !open;
  };
  trigger.addEventListener("click", (e) => {
    e.stopPropagation();
    setOpen(root.dataset.open !== "true");
  });
  // Listener sul dialog (non su document): si dismette con l'innerHTML al
  // prossimo renderApp/logout, niente leak di listener globali.
  boundary.addEventListener("click", (e) => {
    if (!root.contains(e.target as Node)) setOpen(false);
  });
  return setOpen;
}

export interface CategorySelect {
  /** Categorie attualmente selezionate (valori). */
  getSelected(): string[];
  /** Deseleziona tutto e aggiorna l'etichetta del trigger. */
  clear(): void;
  /** Imposta la selezione da fuori (es. cambio foto attiva nella coda upload). */
  setSelected(values: string[]): void;
}

/** Cabla un dropdown categorie (`.select` generato da categorySelectMarkup):
 * apertura/chiusura, toggle delle opzioni con checkbox, e l'etichetta riassuntiva
 * sul trigger. `onChange` è invocato dopo ogni cambio (es. per il filtro live). */
export function wireCategorySelect(
  root: HTMLElement,
  boundary: HTMLElement,
  onChange?: () => void,
): CategorySelect {
  wireDropdownToggle(root, boundary);
  const valueEl = root.querySelector<HTMLSpanElement>(".select__value")!;

  const getSelected = (): string[] =>
    [...root.querySelectorAll<HTMLButtonElement>('[data-cat][aria-selected="true"]')].map(
      (b) => b.dataset.cat ?? "",
    );

  const updateLabel = (): void => {
    const sel = getSelected();
    valueEl.textContent =
      sel.length === 0
        ? (valueEl.dataset.placeholder ?? "")
        : sel.length === 1
          ? (CATEGORIES.find((c) => c.value === sel[0])?.label ?? "")
          : `${sel.length} categorie`;
    valueEl.classList.toggle("is-placeholder", sel.length === 0);
  };

  root.querySelectorAll<HTMLButtonElement>("[data-cat]").forEach((opt) => {
    opt.addEventListener("click", () => {
      const on = opt.getAttribute("aria-selected") === "true";
      opt.setAttribute("aria-selected", String(!on));
      opt.classList.toggle("is-selected", !on);
      updateLabel();
      onChange?.();
    });
  });
  updateLabel();

  return {
    getSelected,
    clear() {
      root.querySelectorAll<HTMLButtonElement>("[data-cat]").forEach((opt) => {
        opt.setAttribute("aria-selected", "false");
        opt.classList.remove("is-selected");
      });
      updateLabel();
    },
    setSelected(values: string[]) {
      const set = new Set(values);
      root.querySelectorAll<HTMLButtonElement>("[data-cat]").forEach((opt) => {
        const on = set.has(opt.dataset.cat ?? "");
        opt.setAttribute("aria-selected", String(on));
        opt.classList.toggle("is-selected", on);
      });
      updateLabel();
    },
  };
}
